/*
 * File: CalendarGrid.tsx
 * Project: a-a
 * File Created: Tuesday, 26th August 2025 12:33:05 am
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 17th October 2025 9:59:35 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import parse from 'html-react-parser';
import zeplinColor from '../../../../../theme/Color';
import styles from './CalendarGrid.module.css';
import CalendarCard from '../../CalendarMain/CalendarCard/CalendarCard';
import CustomPopOver from '../../../../shared/CustomPopOver/CustomPopOver';
import CustomButton from '../../../../shared/CustomButton/CustomButton';
import CustomAlert from '../../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../../shared/CustomAlert/CustomAlertContain';

interface CalendarGridProps {
  startDate: Dayjs;
  endDate: Dayjs;
  timeSlotHeight?: number;
  startHour?: number;
  endHour?: number;
  selectedDate: dayjs.Dayjs | null;
  events: any[];
  getTimezoneDisplayText: () => string;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  startDate,
  endDate,
  timeSlotHeight = 60,
  startHour = 0,
  endHour = 23,
  selectedDate,
  events = [],
  getTimezoneDisplayText,
}) => {
  const theme = useTheme();
  // const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  //  MUI breakpoint queries
  const isXS = useMediaQuery(theme.breakpoints.up('xs')); // 0-600px

  const isXL = useMediaQuery(theme.breakpoints.up('xl')); // 1920px+
  const rowHeight = isXS ? 70 : 115;

  const mockEvents = useMemo(
    () => [
      {
        id: '1',
        title: 'Team Meeting',
        startTime: startDate.hour(9).minute(0),
        endTime: startDate.hour(11).minute(0),
        color: zeplinColor.CalendarCard1,
        type: 'zoom' as const,
      },
      {
        id: '2',
        title: 'Client Call',
        startTime: startDate.hour(11).minute(0),
        endTime: startDate.hour(12).minute(30), // 1.5 hours
        color: zeplinColor.Success,
        type: 'google-meet' as const,
      },
      {
        id: '3',
        title: 'Project Review',
        startTime: startDate.hour(14).minute(15),
        endTime: startDate.hour(15).minute(0), // 45 minutes
        // endTime: startDate.add(1, 'day').hour(17).minute(45), // 1 hour 45 minutes
        color: zeplinColor.CalendarCard1,
        type: 'other' as const,
      },
      {
        id: '4',
        title: 'Project Review',
        startTime: startDate.add(1, 'day').hour(14).minute(15), //  August 28th at 2:15 PM
        endTime: startDate.add(1, 'day').hour(16).minute(15), //  August 28th at 3:00 PM (45 minutes)
        color: zeplinColor.CalendarCard1,
        type: 'other' as const,
      },
      {
        id: '5',
        title: 'Project Review next',
        startTime: startDate.add(2, 'day').hour(14).minute(15), //  August 28th at 2:15 PM
        endTime: startDate.add(2, 'day').hour(16).minute(0), //  August 28th at 3:00 PM (45 minutes)
        color: zeplinColor.CalendarCard1,
        type: 'other' as const,
      },
    ],
    [startDate],
  );

  const [copy, setCopy] = useState('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<any>(null);
  const [showGuests, setShowGuests] = React.useState(false);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const handleShowGuests = () => {
    setShowGuests((prev) => !prev);
  };
  const gridBodyRef = useRef<HTMLDivElement>(null);

  const transformZoomEvent = (zoomEvent: any) => {
    // Parse Zoom's ISO datetime strings
    const startTime = dayjs(zoomEvent.start);
    const endTime = dayjs(zoomEvent.end);

    return {
      id: zoomEvent.id,
      title: zoomEvent.meetingName || 'Untitled Meeting',
      startTime,
      endTime,
      color: zeplinColor.USER_PROFILE_COLOR3, // You can set a unique color for Zoom if you want
      type: 'zoom',
      description: zoomEvent.description || '',
      location: zoomEvent.location || '',
      meetingLink: zoomEvent.meetingLink,
      organizer: zoomEvent.organizer?.id || 'Unknown Organizer',
      invitees: zoomEvent.invitees || [],
      status: zoomEvent.status || 'confirmed',
    };
  };
  const transformMicrosoftEvent = (msEvent: any) => {
    // Parse Microsoft's ISO datetime strings
    const startTime = dayjs(msEvent.start);
    const endTime = dayjs(msEvent.end);

    // Determine event type - Microsoft events are typically Teams meetings
    let eventType: 'zoom' | 'google-meet' | 'microsoft-teams' | 'other' = 'microsoft-teams';

    // Check if there's a meeting link to determine type more accurately
    if (msEvent.meetingLink || msEvent.onlineMeeting) {
      if (msEvent.meetingLink?.includes('teams.microsoft.com') || msEvent.onlineMeeting) {
        eventType = 'microsoft-teams';
      } else if (msEvent.meetingLink?.includes('meet.google.com')) {
        eventType = 'google-meet';
      } else if (msEvent.meetingLink?.includes('zoom.us')) {
        eventType = 'zoom';
      }
    }

    // Assign color based on event type

    return {
      id: msEvent.id || `ms-${msEvent.subject}-${msEvent.start}`, // Generate ID if not provided
      title: msEvent.subject || 'Untitled Meeting',
      startTime,
      endTime,
      color: zeplinColor.CalendarCard2, // Default color for Microsoft Teams
      type: eventType,
      description: msEvent.body || '',
      location: msEvent.location || '',
      meetingLink: msEvent.meetingLink || msEvent.onlineMeeting?.joinUrl || '',
      organizer: msEvent.organizer || 'Unknown Organizer',
      invitees: msEvent.invitees || [],
      status: msEvent.status || 'confirmed',
    };
  };
  const transformGoogleEvent = (googleEvent: any) => {
    // Parse Google's ISO datetime strings
    const startTime = dayjs(googleEvent.start);
    const endTime = dayjs(googleEvent.end);

    // Determine event type based on meeting link
    let eventType: 'zoom' | 'google-meet' | 'other' = 'other';
    if (googleEvent.meetingLink) {
      if (googleEvent.meetingLink.includes('meet.google.com')) {
        eventType = 'google-meet';
      } else if (googleEvent.meetingLink.includes('zoom.us')) {
        eventType = 'zoom';
      }
    }

    // Assign color based on event type or organizer

    return {
      id: googleEvent.id,
      title: googleEvent.meetingName || 'Untitled Meeting',
      startTime,
      endTime,
      color: zeplinColor.CalendarCard1, // eventColor,
      type: eventType,
      description: googleEvent.description,
      location: googleEvent.location,
      meetingLink: googleEvent.meetingLink,
      organizer: googleEvent.organizer.email,
      invitees: googleEvent.invitees,
      status: googleEvent.status,
    };
  };
  const processedEvents = useMemo(() => {
    let allEvents: any = [];

    // Transform events based on their structure
    if (events && events.length > 0) {
      console.log('Processing events:', events);

      const transformedEvents = events
        .map((event: any) => {
          try {
            // Check if it's a Microsoft event (has 'subject' property)
            if (Object.keys(event).includes('body')) {
              console.log('Transforming Microsoft event:', event);
              return transformMicrosoftEvent(event);
            }
            // Check if it's a Google event (has 'meetingName' or different structure)
            else if (event.type === 'Google') {
              console.log('Transforming Google event:', event);
              return transformGoogleEvent(event);
            }
            // Generic transformation for other event types
            else {
              console.log('Transforming generic event:', event);
              return transformZoomEvent(event);
            }
          } catch (error) {
            console.error('Error transforming event:', event, error);
            return null;
          }
        })
        .filter(Boolean);

      allEvents = [...transformedEvents];
    }

    // Add mock events for development (you can remove this in production)
    // if (allEvents.length === 0) {
    //   console.log('No events found, using mock events');
    //   allEvents = mockEvents;
    // }

    return allEvents;
  }, [events]);

  const eventsToUse = processedEvents;
  // const eventsToUse = mockEvents;
  //  Helper function to get events for specific date and time
  const getEventsForSlot = (date: Dayjs, hour: number) => {
    return eventsToUse.filter((event: any) => {
      const eventDate = event.startTime.format('YYYY-MM-DD');
      const slotDate = date.format('YYYY-MM-DD');
      const eventStartHour = event.startTime.hour();

      return eventDate === slotDate && eventStartHour === hour;
    });
  };
  //  Generate time slots (24 hours)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push({
        time: dayjs().hour(hour).minute(0).format('h:mm a'),
        hour24: String(hour).padStart(2, '0') + ':00',
        hourIndex: hour,
      });
    }
    return slots;
  }, [startHour, endHour]);
  //  Generate date columns
  const dateColumns = useMemo(() => {
    const columns = [];
    let currentDate = startDate;

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      columns.push({
        date: currentDate,
        dayName: currentDate.format('ddd'),
        dayNumber: currentDate.format('DD'),
        fullFormat: currentDate.format('ddd DD'),
        isToday: currentDate.isSame(dayjs(), 'day'),
        isSelected: selectedDate
          ? currentDate.isSame(selectedDate, 'day')
          : dayjs().isSame(currentDate, 'day'),
      });
      currentDate = currentDate.add(1, 'day');
    }
    return columns;
  }, [startDate, endDate]);

  const timeColumnWidth = 150;
  const dateColumnWidth = 184; // `calc((100% - ${timeColumnWidth}px) / ${dateColumns.length})`;
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCopyLink = (val: any) => {
    // console.log() .
    navigator.clipboard.writeText(val);
    setCopy('Copied link');
  };
  const hourEvents: { [key: string]: number } = {};
  useEffect(() => {
    // Scroll to 9:00am row after mount
    if (gridBodyRef.current) {
      // Find the row index for 9:00am
      const nineAMIndex = timeSlots.findIndex((slot) => slot.hour24 === '09:00');
      if (nineAMIndex !== -1) {
        const scrollY = nineAMIndex * rowHeight;
        gridBodyRef.current.scrollTop = scrollY;
      }
    }
  }, [rowHeight, timeSlots]);
  return (
    <Box
      className={styles['calendar-grid']}
      sx={{
        height: '100%',
        // overflowX: 'auto',
        backgroundColor: zeplinColor.Neutral1,
      }}
    >
      {/* Header Row with Dates */}
      <Box
        sx={{
          display: 'flex',
          position: 'sticky',
          top: 0,
          zIndex: 101,
          backgroundColor: zeplinColor.Neutral1,
          // borderBottom: `1px solid ${zeplinColor.Neutral5}`,
          minWidth: 'max-content',
          // overflowX: 'auto',
          //   minHeight: '60px',
        }}
      >
        {/* Empty corner cell */}
        <Box
          sx={{
            width: `${timeColumnWidth}px`,
            minWidth: `${timeColumnWidth}px`,
            // maxWidth: `${timeColumnWidth}px`,
            borderRight: `1px solid ${zeplinColor.Neutral5}`,
            backgroundColor: zeplinColor.Neutral1,
            // display: 'flex',
            // alignItems: 'center',
            // justifyContent: 'center',
            position: 'sticky',
            left: 0,
            padding: '16px 16px 5px 16px',
          }}
        >
          <Box
            sx={{
              backgroundColor: zeplinColor.Neutral3,
              padding: { xs: '12px 4px', xl: '14px 8px' },
              // width: `${timeColumnWidth}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '14px',
              // zIndex: 101,
              position: 'relative',
            }}
          >
            <Typography
              variant='responsiveBody3'
              sx={{
                color: zeplinColor.BasicBlack,
                lineHeight: '29.3px',
                // zIndex: 101,
                position: 'relative',
              }}
            >
              {getTimezoneDisplayText()}
              {/* GMT +2400:30 */}
            </Typography>
          </Box>
        </Box>

        {/* Date Header Columns */}
        {dateColumns.map((dateCol, index) => {
          return (
            <Box
              key={dateCol.date.format('YYYY-MM-DD')}
              sx={{
                // width: dateColumnWidth,
                // width: `${dateColumnWidth * dateColumns.length}px`,
                // borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                minWidth: `${dateColumnWidth}px`,
                flex: 1,
                //   minWidth: '120px',
                borderRight:
                  index === dateColumns.length - 1 ? 'none' : `1px solid ${zeplinColor.Neutral5}`,
                backgroundColor: zeplinColor.Neutral1,

                padding: '16px 16px 5px 16px',
              }}
            >
              <Box
                sx={{
                  padding: { xs: '12px 4px', xl: '14px 8px' },
                  borderRadius: '14px',
                  display: 'flex',
                  //   flexDirection: 'column',
                  backgroundColor: dateCol.isSelected
                    ? zeplinColor.BasicBlack
                    : zeplinColor.Neutral3,
                  gap: '12px',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant='caption'
                  sx={{
                    color: dateCol.isSelected ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                    // fontSize: '12px',
                    // fontWeight: 500,
                  }}
                >
                  {dateCol.dayName.toUpperCase()}
                </Typography>
                <Typography
                  variant='responsiveDisplay6'
                  sx={{
                    color: dateCol.isSelected ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
                    // fontWeight: 600,
                    // fontSize: '18px',
                    // lineHeight: 1,
                  }}
                >
                  {dateCol.dayNumber}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Time Grid Body */}
      <Box
        ref={gridBodyRef}
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: `${timeColumnWidth + dateColumnWidth * dateColumns.length}px`, //  Minimum width for scroll
          // width: `${timeColumnWidth + dateColumnWidth * dateColumns.length}px`, //  Fixed width
          // overflowX: 'auto', //  Enable horizontal scrolling
          // overflowY: 'auto', //  Keep vertical scrolling
        }}
      >
        {timeSlots.map((timeSlot, rowIndex) => (
          <Box
            className={styles['time-slot-row']}
            key={timeSlot.hour24}
            sx={{
              display: 'flex',
              width: '100%',
              // minHeight: `${timeSlotHeight}px`,
              height: rowHeight,
              // top: 99.9,
              // borderBottom: `1px solid ${zeplinColor.Neutral5}`,
              // '&:hover': {
              //   backgroundColor: zeplinColor.Neutral3,
              // },
            }}
          >
            {/* Time Column */}
            <Box
              sx={{
                width: `${timeColumnWidth}px`,
                minWidth: `${timeColumnWidth}px`,
                // maxWidth: `${timeColumnWidth}px`,
                borderRight: `1px solid ${zeplinColor.Neutral5}`,
                backgroundColor: zeplinColor.Neutral1,
                display: 'flex',
                // alignItems: 'center',
                justifyContent: 'center',

                // paddingRight: '12px',
                // paddingTop: '8px',
                padding: { xs: '10px 10px 10px 10px', xl: '16px 16px 16px 16px' },
                position: 'sticky',
                left: 0,
                zIndex: 100,
              }}
            >
              <Box
                sx={{
                  //   backgroundColor: zeplinColor.Neutral3,
                  padding: { xs: '0px 8px 12px 8px', xl: '0px 8px 24px 8px' },

                  //   borderRadius: '14px',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    color: zeplinColor.Neutral8,
                    // fontSize: '12px',
                    marginTop: '-10px',
                    fontWeight: 500,
                  }}
                >
                  {timeSlot.time}
                </Typography>
              </Box>
            </Box>
            {/* Date/Event Columns */}
            {/*  Replace hard-coded events with dynamic rendering */}
            {dateColumns.map((dateCol, colIndex) => {
              //  Get events for this specific date and time slot
              const slotEvents = getEventsForSlot(dateCol.date, timeSlot.hourIndex);
              const isCurrentDate = dateCol.date.isSame(dayjs(), 'day');
              const isCurrentHour = timeSlot.hourIndex === dayjs().hour();
              return (
                <Box
                  className={styles['grid-cell']}
                  sx={{
                    minWidth: `${dateColumnWidth}px`,
                    paddingTop: '16px',
                    flex: 1,
                    borderRight:
                      colIndex === dateColumns.length - 1
                        ? 'none'
                        : `1px solid ${zeplinColor.Neutral5}`,
                  }}
                >
                  <Box
                    key={`${dateCol.date.format('YYYY-MM-DD')}-${timeSlot.hour24}`}
                    sx={{
                      borderTop: `1px solid ${zeplinColor.Neutral5}`,
                      paddingTop: '16px',
                      // width: dateColumnWidth,
                      // minWidth: '120px',
                      // borderRight:
                      //   colIndex === dateColumns.length - 1
                      //     ? 'none'
                      //     : `1px solid ${zeplinColor.Neutral5}`,
                      backgroundColor: zeplinColor.Neutral1,
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover': {
                        // backgroundColor: zeplinColor.Neutral3,
                      },
                      '&::after': {
                        content: '""',
                        // position: 'absolute',
                        // top: '50%',
                        // left: 0,
                        // right: 0,
                        // height: '1px',
                        // backgroundColor: zeplinColor.Neutral4,
                        // opacity: 0.3,
                      },
                    }}
                    onClick={() => {
                      console.log('Clicked:', {
                        date: dateCol.date.format('YYYY-MM-DD'),
                        time: timeSlot.hour24,
                        datetime: dateCol.date.hour(timeSlot.hourIndex).format(),
                        hasEvents: slotEvents.length > 0,
                        events: slotEvents.map((e: any) => e.title),
                      });
                    }}
                  >
                    {isCurrentDate && isCurrentHour && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: dayjs().minute() * (rowHeight / 60) + 'px',
                          // height: '2px',
                          // backgroundColor: zeplinColor.BasicBlack,
                          zIndex: 100,
                          // borderRadius: '2px',
                        }}
                      >
                        <img
                          src={'/assets/time-indicator.svg'}
                          alt={'time-indicator'}
                          width={'100%'}
                        />
                      </Box>
                    )}
                    {/*  Dynamic event rendering based on mockEvents */}
                    {slotEvents.map((event: any, eventIndex: number) => {
                      // const dimensions = calculateEventDimensions(event);
                      const meetingDurationHours =
                        event.endTime.diff(event.startTime, 'minute') / 60;
                      const calculatedHeight = meetingDurationHours * rowHeight; // 128px per hour
                      const startMinutes = event.startTime.minute(); // Get minutes (0-59)
                      const minutePercentage = startMinutes / 60; // Convert to percentage (0-1)
                      const topOffset = startMinutes * (rowHeight / 60);
                      const eventSlotKey = `${event.startTime.format(
                        'YYYY-MM-DD',
                      )}-${event.startTime.hour()}`;

                      // Use a map/object to track counts per date-hour
                      if (!hourEvents[eventSlotKey]) {
                        hourEvents[eventSlotKey] = 0;
                      }
                      const leftPos = hourEvents[eventSlotKey];
                      hourEvents[eventSlotKey] += 1;
                      console.log(
                        startMinutes,
                        event.startTime.hour(),
                        event.startTime.format('h:mm A'),
                        'events start',
                        hourEvents,
                      );
                      return (
                        <Box
                          key={event.id}
                          className={styles['event-item']}
                          sx={{
                            position: 'absolute',
                            top: `${topOffset}px`,
                            left: `${leftPos * 20}px`,
                            right: '0px',
                            // borderTop: `1px solid ${zeplinColor.Neutral5}`,
                            // bottom: '0px',
                            // height: `${dimensions.height - 8}px`, //  Dynamic height based on duration
                            // backgroundColor: event.color,
                            // borderRadius: '8px',
                            // ...(leftPos > 0 && { border: `1.5px solid ${zeplinColor.Neutral1}` }),
                            padding: '0px 8px',
                            boxShadow: zeplinColor.Neutral1,
                            // border: `1px solid ${event.color}`,
                            cursor: 'pointer',
                            zIndex: eventIndex + 1,
                            // overflow: 'hidden',
                            // '&:hover': {
                            //   transform: 'scale(1.02)',
                            //   zIndex: 10,
                            //   boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            // },
                            // transition: 'all 0.2s ease-in-out',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClick(e);
                            setSelectedEvent(event);
                          }}
                        >
                          {calculatedHeight >= rowHeight ? (
                            <CalendarCard
                              height={calculatedHeight}
                              title={event.title}
                              link={event.meetingLink}
                              leftPos={leftPos}
                              // title={event.title}
                              color={event.color}
                              startTime={event.startTime}
                              endTime={event.endTime}
                              invitees={event.invitees}
                              // duration={dimensions.durationHours}
                              // type={event.type}
                              // color={event.color}
                              // height={dimensions.height - 8}
                            />
                          ) : (
                            <Box
                              sx={{
                                padding: '2px 12px 2px 12px',
                                ...(leftPos > 0 && {
                                  border: `1.5px solid ${zeplinColor.Neutral1}`,
                                }),
                                borderRadius: '14px',
                                backgroundColor: event.color || zeplinColor.CalendarCard1,
                                maxWidth: dateColumnWidth - 16,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                              <Typography variant='responsiveBody3'>{event.title}</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
      <CustomPopOver
        setAnchorEl={setAnchorEl}
        vertical='top'
        horizontal='left'
        anchorEl={anchorEl}
        id={id}
        sx={{
          borderRadius: '14px',
          boxShadow: 'rgba(0, 0, 0, 0.15)',
          marginTop: '0px',

          marginLeft: '-22rem',
        }}
      >
        <Box
          sx={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            maxWidth: '350px',
            // textWrap: 'break-word',
            maxHeight: '30rem',
            // overflowY: 'auto',
            wordWrap: 'break-word',
            position: 'relative',
            // backgroundColor: selectedEvent?.color,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              // maxWidth: '350px',
              // textWrap: 'break-word',
              wordWrap: 'break-word',
              // position: 'relative',

              // backgroundColor: selectedEvent?.color,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                position: 'sticky',
                top: 0,
                backgroundColor: zeplinColor.Neutral1,
                zIndex: 10,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '8px',

                  // alignItems: 'center',
                }}
              >
                <Typography variant='responsiveHeading1' color={zeplinColor.BasicBlack}>
                  {selectedEvent?.title}
                </Typography>
                <img
                  src={'/assets/close-icon.svg'}
                  // className={style['icon']}
                  alt='close'
                  style={{ cursor: 'pointer', width: '24px', height: '24px', marginTop: '5px' }}
                  onClick={() => setAnchorEl(null)}
                />
              </Box>
              <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
                {dayjs(selectedEvent?.startTime).format('dddd, MMMM D, YYYY | h:mm a')} -{' '}
                {dayjs(selectedEvent?.endTime).format('h:mm a')}
              </Typography>
            </Box>
            {selectedEvent?.description ? (
              <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack} sx={{}}>
                {parse(selectedEvent?.description || '')}
              </Typography>
            ) : (
              ''
            )}
          </Box>
          <Box sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <a
                href={selectedEvent?.meetingLink}
                target='_blank'
                rel='noopener noreferrer'
                style={{ textDecoration: 'none' }}
              >
                <CustomButton
                  disabled={false}
                  onClick={() => {}}
                  variant='contained'
                  color={'primary'}
                  style={{
                    // width: '50%',
                    borderRadius: '14px',
                    // height: 56,
                    // '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                    padding: '12px 24px',
                  }}
                  textStyle={{ color: zeplinColor.Neutral1, fontSize: '14px', lineHeight: '0%' }}
                  text={'Join Meeting'}
                  icon={<img src={'/assets/video-call-light.svg'} />}
                  // tabindex={4}
                />
              </a>
              <img
                src={'/assets/content_copy.svg'}
                alt='copy'
                style={{ cursor: 'pointer' }}
                width='24px'
                height='24px'
                onClick={() => handleCopyLink(selectedEvent?.meetingLink)}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                // alignItems: 'center',
                cursor: 'pointer',
                gap: '16px',
              }}
              onClick={handleShowGuests}
            >
              <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'flex-start' }}>
                <img
                  src={'/assets/user-multiple-02.svg'}
                  alt='guest'
                  style={{ cursor: 'pointer' }}
                  width='24px'
                  height='24px'
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    // flexWrap: 'wrap',
                    maxWidth: '200px',
                  }}
                >
                  <Typography variant='responsiveBody2'>
                    {selectedEvent?.invitees.length} Guests
                  </Typography>
                  {showGuests &&
                    selectedEvent?.invitees.map((invitee: any, key: number) => (
                      <Box
                        key={invitee.id}
                        sx={{
                          display: 'flex',
                          gap: '16px',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          sx={{
                            borderRadius: '100px',
                            padding: '10px',
                            width: { xs: '32px', xl: '32px' },
                            height: { xs: '32px', xl: '32px' },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: invitee.profileImage
                              ? 'transparent'
                              : zeplinColor[
                                  key % 8 === 0
                                    ? 'Brand1'
                                    : (`USER_PROFILE_COLOR${key % 8}` as keyof typeof zeplinColor)
                                ],
                            position: 'relative',
                          }}
                        >
                          {invitee.profileImage ? (
                            <img
                              src={invitee.profileImage}
                              alt='guest'
                              style={{ cursor: 'pointer', borderRadius: '50%' }}
                              width='32px'
                              height='32px'
                            />
                          ) : (
                            <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                              {invitee.name
                                ?.split(' ')
                                .map((value: any, index: number) =>
                                  invitee.name &&
                                  (index === 0 || index === invitee.name?.split(' ').length - 1)
                                    ? value[0].toUpperCase()
                                    : '',
                                )}
                            </Typography>
                          )}
                          {invitee?.confirmation !== 'needsAction' &&
                            invitee?.confirmation !== 'none' && (
                              <img
                                src={
                                  invitee?.confirmation === 'accepted'
                                    ? '/assets/event-checked.svg'
                                    : invitee?.confirmation === 'declined'
                                    ? '/assets/event-not-checked.svg'
                                    : ''
                                }
                                alt='confirmation'
                                style={{ position: 'absolute', bottom: 0, right: 0 }}
                                width='14px'
                                height='14px'
                              />
                            )}
                        </Box>
                        <Typography
                          variant='responsiveBody3'
                          color={zeplinColor.BasicBlack}
                          sx={{ wordWrap: 'break-word' }}
                        >
                          {invitee.name}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>

              <img
                src={showGuests ? '/assets/up-arrow.svg' : '/assets/down-arrow.svg'}
                // onClick={handleShowGuests}
                alt='guest'
                style={{ cursor: 'pointer' }}
                width='24px'
                height='24px'
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '16px' }}>
              <img
                src={'/assets/calendar.svg'}
                alt='guest'
                style={{ cursor: 'pointer' }}
                width='24px'
                height='24px'
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  wordWrap: 'break-word',
                }}
              >
                <Typography variant='responsiveBody2' sx={{ wordBreak: 'break-word' }}>
                  {selectedEvent?.organizer}
                </Typography>
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                  Created by
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CustomPopOver>
      <CustomAlert
        open={copy ? true : false}
        handleClose={() => setCopy('')}
        message={
          <CustomAlertContain
            alert={copy && 'Link Copied'}
            alertIcon={copy && <img src='/assets/checkmark-green.png' width={24} height={24} />}
          />
        }
        error={false}
      />
    </Box>
  );
};

export default CalendarGrid;
